import React, { useContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import iconwhatsapp from "../assets/images/icons/whatsapp.svg";
import iconsearch from "../assets/images/icons/search.svg";
import ReactGA from "react-ga4";
import axios from "../axios";
import { useBusinessNameContext } from "../context/BusinessName";
import { ThemeContext } from "../context/ThemeContext";
import { extractChatUuid, setStoredChatUuid } from "../chat/chatIds";
import { CHAT_OPTIONS_SEGMENT, chatOptionsPath } from "../chat/chatRoutes";

function Footer() {
  const [hideFooter, setHideFooter] = useState(false);
  const [isWhatsapp, setIsWhatsapp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [roomNumber, setRoomNumber] = useState();
  const [color, setColor] = useState();
  const [businessType, setBusinessType] = useState("");

  const { setTheme } = useContext(ThemeContext);
  const { businessName, setBusinessName } = useBusinessNameContext();

  const location = useLocation();
  const rawPath = location.pathname;
  const room_uuid = rawPath.split("/")[1];
  const path = rawPath.split("/")[2];

  const isOrdersActive = path === "MyOrders";
  const isChatActive = path === "chat" || path === CHAT_OPTIONS_SEGMENT;

  const isHomeActive = !isOrdersActive && !isChatActive;
  const { theme } = useContext(ThemeContext);
  const navigate = useNavigate();

  const navigateTo = (location) => {
    navigate(location);
  };

  // Check if footer should be hidden based on current path
  useEffect(() => {
    const shouldHideFooter =
      location.pathname.includes("/RestaurantSubMenu") ||
      location.pathname.includes("/PharmacySubMenu") ||
      location.pathname.includes("/FlowersSubMenu") ||
      location.pathname.includes("/SupermarketSubMenu") ||
      location.pathname.includes("/restaurant-menu/categories") ||
      location.pathname.includes("/restaurants/search") ||
      location.pathname.includes("/restaurant-menu") ||
      location.pathname.includes("/rstauran-menu-categories") ||
      location.pathname.includes("/rstauran-menu-subcategories") ||
      location.pathname.includes("/rstauran-menu-items") ||
      location.pathname.includes("/Checkout-dining") ||
      location.pathname.includes("/qr-scan") ||
      location.pathname.includes("/RedirectTaxi") ||
      location.pathname.includes("/scan-qr-code") ||
      location.pathname.includes("/not-found") ||
      location.pathname.includes("/food-and-beverages") ||
      location.pathname.includes("/cart") ||
      location.pathname.includes("/search") ||
      (location.pathname.includes("/hotel-services") &&
        !location.pathname.includes("/hotel-services/laundry")) ||
      location.pathname.includes("/qrcode") ||
      location.pathname.includes("/restaurant/combo-meals") ||
      location.pathname.includes("/taxi-yango") ||
      location.pathname.includes("/chat") ||
      location.search.includes("Restaurant&in_dining");

    setHideFooter(shouldHideFooter);
  }, [location.pathname, location.search]);

  // Get footer-specific data
  useEffect(() => {
    const getFooterData = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`/${room_uuid}/footer/qr-code`);
        const data = res.data.data;
        const resolvedChatUuid = extractChatUuid(data);
        if (resolvedChatUuid) {
          setStoredChatUuid(room_uuid, resolvedChatUuid);
        }
        // Set states for footer display
        setBusinessType(data.businessType);
        setRoomNumber(data?.room_number);
        setIsWhatsapp(data?.whatsapp_enabled);
        setColor(data?.color);

        // Update context if needed (fallback in case Layout hasn't loaded yet)
        if (!businessName) {
          setTheme(data?.use_new_design);
          setBusinessName(data?.business_name);
        }

        // Set CSS variables if not already set
        if (
          data?.color &&
          !document.documentElement.style.getPropertyValue("--color")
        ) {
          document.documentElement.style.setProperty("--color", data?.color);
          document.documentElement.style.setProperty(
            "--color_t",
            `${data?.color}b3`
          );
          document.documentElement.style.setProperty(
            "--header_color",
            data?.header_color
          );
        }
      } catch (err) {
        console.error("Error fetching footer data", err);
      } finally {
        setLoading(false);
      }
    };

    if (room_uuid) {
      getFooterData();
    }
  }, [room_uuid, businessName, setTheme, setBusinessName]);

  return (
    <>
      {!hideFooter && (
        <>
          {!loading && theme !== true && (
            <div className="footer notransition">
              {/* Menu Link */}
              <div>
                <Link
                  to={`/${room_uuid}/MyOrders`}
                  className={`footer-link ${
                    isOrdersActive ? "active-footer" : ""
                  }`}
                  onClick={() => {
                    ReactGA.event({
                      category: "Navbar Link",
                      action: "Click",
                      value: "menu",
                    });
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M21.9707 18V19C21.9707 20.65 21.9707 22 18.9707 22H4.9707C1.9707 22 1.9707 20.65 1.9707 19V18C1.9707 17.45 2.4207 17 2.9707 17H20.9707C21.5207 17 21.9707 17.45 21.9707 18Z"
                      fill="currentColor"
                    />
                    <path
                      d="M14.4095 5.18002C14.4595 4.98002 14.4895 4.79002 14.4995 4.58002C14.5295 3.42002 13.8195 2.40002 12.6995 2.10002C11.0195 1.64002 9.49953 2.90002 9.49953 4.50002C9.49953 4.74002 9.52953 4.96002 9.58953 5.18002C5.97953 5.95002 3.26953 9.16002 3.26953 13V14.5C3.26953 15.05 3.71953 15.5 4.26953 15.5H19.7195C20.2695 15.5 20.7195 15.05 20.7195 14.5V13C20.7195 9.16002 18.0195 5.96002 14.4095 5.18002ZM14.9995 11.75H8.99953C8.58953 11.75 8.24953 11.41 8.24953 11C8.24953 10.59 8.58953 10.25 8.99953 10.25H14.9995C15.4095 10.25 15.7495 10.59 15.7495 11C15.7495 11.41 15.4095 11.75 14.9995 11.75Z"
                      fill="currentColor"
                    />
                  </svg>
                  Orders
                </Link>
              </div>

              {/* Home Link */}
              <div
                onClick={() => {
                  navigateTo(`/${room_uuid}/qr-code`);
                  ReactGA.event({
                    category: "Navbar Link",
                    action: "Click",
                    value: "home",
                  });
                }}
              >
                <Link
                  className={`footer-link ${
                    isHomeActive ? "active-footer" : ""
                  }`}
                  style={{ zIndex: "100", pointerEvents: "none" }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M10.043 3.14941C11.0596 2.33305 12.653 2.28692 13.7549 3.00684L13.9688 3.16113L20.5137 8.39648V8.39746C20.8916 8.70556 21.2196 9.18882 21.4307 9.74121C21.6407 10.2911 21.7189 10.8673 21.6465 11.3477L20.3877 18.8848C20.1386 20.3182 18.7435 21.5 17.2998 21.5H6.7002C5.327 21.5 4.04309 20.4867 3.68262 19.1641L3.62305 18.8955L2.36328 11.3574V11.3564L2.33984 11.1709C2.30172 10.7279 2.38061 10.2244 2.56445 9.74219C2.74829 9.2601 3.02465 8.83066 3.34961 8.52441L3.49219 8.40039L10.043 3.15039V3.14941ZM12 13.75C11.3139 13.75 10.75 14.3139 10.75 15V18C10.75 18.6861 11.3139 19.25 12 19.25C12.6861 19.25 13.25 18.6861 13.25 18V15C13.25 14.3139 12.6861 13.75 12 13.75Z"
                      fill="currentColor"
                      stroke="currentColor"
                    />
                  </svg>
                  Home
                </Link>
              </div>

              {/* Search Link */}
              {/* {businessType !== "Hospital" && (
                <div>
                  <Link
                    to={`/${room_uuid}/SearchPage`}
                    className={path === "SearchPage" ? "active-footer" : null}
                    onClick={() => {
                      ReactGA.event({
                        category: "Navbar Link",
                        action: "Click",
                        value: "search page",
                      });
                    }}
                  >
                    <img
                      src={iconsearch}
                      alt="Search"
                      className="search-img"
                      width="23px"
                      height="auto"
                    />
                  </Link>
                </div>
              )} */}

              {/* Chat: chooser between Servy and the host chat */}
              {isWhatsapp && (
                <div>
                  <Link
                    to={chatOptionsPath(room_uuid)}
                    className={`footer-link ${
                      isChatActive ? "active-footer" : ""
                    }`}
                    onClick={() => {
                      ReactGA.event({
                        category: "Navbar Link",
                        action: "Click",
                        value: "host-chat",
                      });
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M17 2H7C4.24 2 2 4.23 2 6.98V12.96V13.96C2 16.71 4.24 18.94 7 18.94H8.5C8.77 18.94 9.13 19.12 9.3 19.34L10.8 21.33C11.46 22.21 12.54 22.21 13.2 21.33L14.7 19.34C14.89 19.09 15.19 18.94 15.5 18.94H17C19.76 18.94 22 16.71 22 13.96V6.98C22 4.23 19.76 2 17 2ZM8 12C7.44 12 7 11.55 7 11C7 10.45 7.45 10 8 10C8.55 10 9 10.45 9 11C9 11.55 8.56 12 8 12ZM12 12C11.44 12 11 11.55 11 11C11 10.45 11.45 10 12 10C12.55 10 13 10.45 13 11C13 11.55 12.56 12 12 12ZM16 12C15.44 12 15 11.55 15 11C15 10.45 15.45 10 16 10C16.55 10 17 10.45 17 11C17 11.55 16.56 12 16 12Z"
                        fill="currentColor"
                      />
                    </svg>
                    Chat
                  </Link>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </>
  );
}

export default Footer;
